import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Zap, FileText, Shield, AlertTriangle, Users, Scale, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function TermsOfService() {
  const lastUpdated = "29 de Março de 2026";
  
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-background/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
              <Zap className="w-6 h-6 text-primary" />
            </div>
            <span className="font-heading font-bold text-lg">Viral Clipes</span>
          </Link>
          <Button variant="ghost" asChild>
            <Link to="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Link>
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold">Termos de Uso</h1>
          </div>
          <p className="text-muted-foreground">Última atualização: {lastUpdated}</p>
        </div>

        <div className="prose prose-invert max-w-none space-y-8">
          {/* 1. Aceitação dos Termos */}
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Scale className="w-5 h-5 text-primary" />
              1. Aceitação dos Termos
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Ao acessar ou usar a plataforma Viral Clipes (&quot;Serviço&quot;), você concorda em estar vinculado a estes 
              Termos de Uso (&quot;Termos&quot;). Se você não concordar com qualquer parte destes termos, não poderá 
              acessar o Serviço. Estes Termos se aplicam a todos os visitantes, usuários e outras pessoas 
              que acessam ou usam o Serviço.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              O uso do Serviço também está sujeito à nossa <Link to="/privacy" className="text-primary hover:underline">Política de Privacidade</Link>, 
              que descreve como coletamos, usamos e compartilhamos suas informações.
            </p>
          </section>

          {/* 2. Descrição do Serviço */}
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Globe className="w-5 h-5 text-primary" />
              2. Descrição do Serviço
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              O Viral Clipes é uma plataforma de automação de conteúdo que permite aos usuários:
            </p>
            <ul className="list-disc list-inside text-muted-foreground mt-4 space-y-2">
              <li>Descobrir e analisar vídeos de podcasts em plataformas de terceiros</li>
              <li>Identificar momentos virais usando inteligência artificial</li>
              <li>Criar clipes curtos otimizados para redes sociais</li>
              <li>Publicar conteúdo em plataformas como YouTube, TikTok e Instagram</li>
              <li>Gerenciar agendamento e análises de desempenho</li>
            </ul>
          </section>

          {/* 3. Requisitos de Elegibilidade */}
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              3. Requisitos de Elegibilidade
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Para usar o Serviço, você deve:
            </p>
            <ul className="list-disc list-inside text-muted-foreground mt-4 space-y-2">
              <li>Ter pelo menos 18 anos de idade ou a maioridade legal em sua jurisdição</li>
              <li>Ter capacidade legal para celebrar um contrato vinculativo</li>
              <li>Não estar proibido de usar o Serviço sob as leis aplicáveis</li>
              <li>Possuir os direitos necessários sobre o conteúdo que processa através do Serviço</li>
            </ul>
          </section>

          {/* 4. Conta do Usuário */}
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4">4. Conta do Usuário</h2>
            <p className="text-muted-foreground leading-relaxed">
              Ao criar uma conta conosco, você garante que:
            </p>
            <ul className="list-disc list-inside text-muted-foreground mt-4 space-y-2">
              <li>As informações fornecidas são precisas, completas e atualizadas</li>
              <li>Manterá a segurança de sua senha e não a compartilhará com terceiros</li>
              <li>Notificará imediatamente sobre qualquer uso não autorizado de sua conta</li>
              <li>É responsável por todas as atividades que ocorrem sob sua conta</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Reservamo-nos o direito de suspender ou encerrar sua conta se qualquer informação 
              fornecida for falsa, imprecisa ou violar estes Termos.
            </p>
          </section>

          {/* 5. Integração com Plataformas de Terceiros */}
          <section className="glass-card p-6 rounded-xl border border-primary/30">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              5. Integração com Plataformas de Terceiros
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-lg mb-2">5.1 YouTube (Google)</h3>
                <p className="text-muted-foreground leading-relaxed">
                  O uso dos serviços do YouTube através do Viral Clipes está sujeito aos:
                </p>
                <ul className="list-disc list-inside text-muted-foreground mt-2 space-y-1">
                  <li><a href="https://www.youtube.com/t/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Termos de Serviço do YouTube</a></li>
                  <li><a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Política de Privacidade do Google</a></li>
                  <li><a href="https://www.youtube.com/howyoutubeworks/policies/community-guidelines/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Diretrizes da Comunidade do YouTube</a></li>
                  <li><a href="https://developers.google.com/youtube/terms/api-services-terms-of-service" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Termos de Serviço da API do YouTube</a></li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-2">
                  Você pode revogar o acesso do Viral Clipes aos seus dados do YouTube a qualquer momento através 
                  das <a href="https://security.google.com/settings/security/permissions" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">configurações de segurança do Google</a>.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-lg mb-2">5.2 TikTok</h3>
                <p className="text-muted-foreground leading-relaxed">
                  O uso dos serviços do TikTok através do Viral Clipes está sujeito aos:
                </p>
                <ul className="list-disc list-inside text-muted-foreground mt-2 space-y-1">
                  <li><a href="https://www.tiktok.com/legal/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Termos de Serviço do TikTok</a></li>
                  <li><a href="https://www.tiktok.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Política de Privacidade do TikTok</a></li>
                  <li><a href="https://www.tiktok.com/community-guidelines" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Diretrizes da Comunidade do TikTok</a></li>
                  <li><a href="https://developers.tiktok.com/doc/tiktok-api-terms-of-service" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Termos de Serviço da API do TikTok</a></li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-lg mb-2">5.3 Instagram (Meta)</h3>
                <p className="text-muted-foreground leading-relaxed">
                  O uso dos serviços do Instagram através do Viral Clipes está sujeito aos:
                </p>
                <ul className="list-disc list-inside text-muted-foreground mt-2 space-y-1">
                  <li><a href="https://help.instagram.com/581066165581870" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Termos de Uso do Instagram</a></li>
                  <li><a href="https://privacycenter.instagram.com/policy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Política de Privacidade do Instagram</a></li>
                  <li><a href="https://help.instagram.com/477434105621119" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Diretrizes da Comunidade do Instagram</a></li>
                  <li><a href="https://developers.facebook.com/terms/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Termos da Plataforma Meta</a></li>
                </ul>
              </div>

              <div className="bg-yellow-500/10 border border-yellow-500/30 p-4 rounded-lg">
                <p className="text-yellow-400 text-sm">
                  <strong>Importante:</strong> Ao conectar suas contas de redes sociais ao Viral Clipes, você autoriza 
                  o acesso às suas contas conforme necessário para fornecer o Serviço. Você é responsável por 
                  garantir que seu uso do Serviço cumpra os termos e políticas de cada plataforma.
                </p>
              </div>
            </div>
          </section>

          {/* 6. Uso Aceitável */}
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-primary" />
              6. Uso Aceitável e Restrições
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Você concorda em NÃO usar o Serviço para:
            </p>
            <ul className="list-disc list-inside text-muted-foreground mt-4 space-y-2">
              <li>Violar quaisquer leis, regulamentos ou direitos de terceiros</li>
              <li>Publicar conteúdo que infrinja direitos autorais, marcas registradas ou propriedade intelectual</li>
              <li>Criar ou distribuir conteúdo ilegal, ofensivo, difamatório, discriminatório ou que incite violência</li>
              <li>Publicar conteúdo sexualmente explícito, pornográfico ou que explore menores</li>
              <li>Enviar spam, phishing ou qualquer forma de comunicação não solicitada</li>
              <li>Tentar acessar sistemas, contas ou dados sem autorização</li>
              <li>Interferir ou interromper a integridade ou desempenho do Serviço</li>
              <li>Usar bots, scrapers ou métodos automatizados não autorizados</li>
              <li>Revender, sublicenciar ou redistribuir o Serviço sem autorização</li>
              <li>Criar contas falsas ou assumir identidades de terceiros</li>
              <li>Manipular métricas, engajamento ou algoritmos de plataformas</li>
              <li>Violar as diretrizes da comunidade do YouTube, TikTok ou Instagram</li>
            </ul>
          </section>

          {/* 7. Propriedade Intelectual e Conteúdo */}
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4">7. Propriedade Intelectual e Conteúdo</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">7.1 Seu Conteúdo</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Você mantém todos os direitos sobre o conteúdo que processa através do Serviço. Ao usar o Serviço, 
                  você nos concede uma licença limitada, não exclusiva e revogável para processar seu conteúdo 
                  conforme necessário para fornecer o Serviço.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold mb-2">7.2 Responsabilidade pelo Conteúdo</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Você declara e garante que:
                </p>
                <ul className="list-disc list-inside text-muted-foreground mt-2 space-y-1">
                  <li>Possui todos os direitos necessários sobre o conteúdo que processa</li>
                  <li>Obteve todas as autorizações, licenças e consentimentos necessários</li>
                  <li>O uso do conteúdo não viola direitos de terceiros</li>
                  <li>O conteúdo está em conformidade com as leis aplicáveis</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-2">7.3 Conteúdo de Terceiros</h3>
                <p className="text-muted-foreground leading-relaxed">
                  O Serviço pode acessar conteúdo de plataformas de terceiros. Esse conteúdo pertence aos 
                  respectivos proprietários e está sujeito às suas políticas. Você é o único responsável 
                  por garantir que tem o direito de usar esse conteúdo.
                </p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">7.4 Nosso Conteúdo</h3>
                <p className="text-muted-foreground leading-relaxed">
                  O Serviço e seu conteúdo original, recursos e funcionalidades são e permanecerão propriedade 
                  exclusiva do Viral Clipes e seus licenciadores. O Serviço é protegido por direitos autorais, 
                  marcas registradas e outras leis.
                </p>
              </div>
            </div>
          </section>

          {/* 8. Planos e Pagamentos */}
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4">8. Planos, Pagamentos e Cancelamento</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">8.1 Assinaturas</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Algumas partes do Serviço são oferecidas mediante assinatura paga. Você será cobrado 
                  antecipadamente em uma base recorrente (mensal ou anual), dependendo do plano selecionado.
                </p>
              </div>
              
              <div>
                <h3 className="font-semibold mb-2">8.2 Renovação Automática</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Sua assinatura será renovada automaticamente ao final de cada período, a menos que você 
                  a cancele antes da data de renovação através das configurações da sua conta.
                </p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">8.3 Reembolsos</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Pagamentos não são reembolsáveis, exceto quando exigido por lei aplicável. Você pode 
                  cancelar sua assinatura a qualquer momento, mas continuará tendo acesso até o final 
                  do período já pago.
                </p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">8.4 Alterações de Preço</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Reservamo-nos o direito de modificar os preços a qualquer momento. Alterações de preço 
                  entrarão em vigor no próximo período de cobrança após notificação.
                </p>
              </div>
            </div>
          </section>

          {/* 9. Isenção de Garantias */}
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4">9. Isenção de Garantias</h2>
            <p className="text-muted-foreground leading-relaxed">
              O SERVIÇO É FORNECIDO &quot;COMO ESTÁ&quot; E &quot;CONFORME DISPONÍVEL&quot;, SEM GARANTIAS DE QUALQUER TIPO, 
              EXPRESSAS OU IMPLÍCITAS. NÃO GARANTIMOS QUE:
            </p>
            <ul className="list-disc list-inside text-muted-foreground mt-4 space-y-2">
              <li>O Serviço funcionará ininterruptamente, de forma segura ou sem erros</li>
              <li>Os resultados obtidos serão precisos ou confiáveis</li>
              <li>A qualidade do Serviço atenderá às suas expectativas</li>
              <li>Quaisquer erros serão corrigidos</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Não somos responsáveis por ações tomadas por plataformas de terceiros (YouTube, TikTok, Instagram), 
              incluindo suspensão de contas, remoção de conteúdo ou alterações em suas APIs.
            </p>
          </section>

          {/* 10. Limitação de Responsabilidade */}
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4">10. Limitação de Responsabilidade</h2>
            <p className="text-muted-foreground leading-relaxed">
              EM NENHUMA CIRCUNSTÂNCIA O VIRAL CLIPES, SEUS DIRETORES, FUNCIONÁRIOS, PARCEIROS, AGENTES, 
              FORNECEDORES OU AFILIADOS SERÃO RESPONSÁVEIS POR QUAISQUER DANOS INDIRETOS, INCIDENTAIS, 
              ESPECIAIS, CONSEQUENCIAIS OU PUNITIVOS, INCLUINDO, SEM LIMITAÇÃO:
            </p>
            <ul className="list-disc list-inside text-muted-foreground mt-4 space-y-2">
              <li>Perda de lucros, dados, uso, ágio ou outras perdas intangíveis</li>
              <li>Suspensão ou encerramento de contas em plataformas de terceiros</li>
              <li>Remoção de conteúdo por plataformas de terceiros</li>
              <li>Alterações nas APIs ou políticas de plataformas de terceiros</li>
              <li>Acesso não autorizado aos seus dados ou contas</li>
              <li>Interrupção ou indisponibilidade do Serviço</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Nossa responsabilidade total não excederá o valor pago por você nos últimos 12 meses.
            </p>
          </section>

          {/* 11. Indenização */}
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4">11. Indenização</h2>
            <p className="text-muted-foreground leading-relaxed">
              Você concorda em defender, indenizar e isentar o Viral Clipes e seus licenciadores, prestadores 
              de serviços, funcionários, agentes, diretores e afiliados de e contra quaisquer reclamações, 
              danos, obrigações, perdas, responsabilidades, custos ou dívidas decorrentes de:
            </p>
            <ul className="list-disc list-inside text-muted-foreground mt-4 space-y-2">
              <li>Seu uso e acesso ao Serviço</li>
              <li>Violação destes Termos</li>
              <li>Violação de direitos de terceiros, incluindo direitos autorais</li>
              <li>Conteúdo que você processa ou publica através do Serviço</li>
              <li>Violação das políticas do YouTube, TikTok ou Instagram</li>
            </ul>
          </section>

          {/* 12. Modificações */}
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4">12. Modificações dos Termos</h2>
            <p className="text-muted-foreground leading-relaxed">
              Reservamo-nos o direito de modificar ou substituir estes Termos a qualquer momento. Se uma 
              revisão for material, forneceremos aviso com pelo menos 30 dias de antecedência antes de 
              quaisquer novos termos entrarem em vigor. O que constitui uma alteração material será 
              determinado a nosso exclusivo critério.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Ao continuar a acessar ou usar nosso Serviço após essas revisões entrarem em vigor, você 
              concorda em estar vinculado aos termos revisados.
            </p>
          </section>

          {/* 13. Rescisão */}
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4">13. Rescisão</h2>
            <p className="text-muted-foreground leading-relaxed">
              Podemos encerrar ou suspender sua conta imediatamente, sem aviso prévio ou responsabilidade, 
              por qualquer motivo, incluindo, sem limitação, se você violar os Termos. Após a rescisão, 
              seu direito de usar o Serviço cessará imediatamente.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Você pode encerrar sua conta a qualquer momento através das configurações da conta ou 
              entrando em contato conosco.
            </p>
          </section>

          {/* 14. Lei Aplicável */}
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4">14. Lei Aplicável e Jurisdição</h2>
            <p className="text-muted-foreground leading-relaxed">
              Estes Termos serão regidos e interpretados de acordo com as leis do Brasil, incluindo o 
              Marco Civil da Internet (Lei nº 12.965/2014) e a Lei Geral de Proteção de Dados (Lei nº 13.709/2018), 
              sem considerar suas disposições sobre conflitos de leis.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-4">
              Qualquer disputa decorrente destes Termos será submetida à jurisdição exclusiva dos tribunais 
              competentes do Brasil.
            </p>
          </section>

          {/* 15. Contato */}
          <section className="glass-card p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4">15. Contato</h2>
            <p className="text-muted-foreground leading-relaxed">
              Se você tiver dúvidas sobre estes Termos de Uso, entre em contato conosco:
            </p>
            <div className="mt-4 p-4 bg-muted/50 rounded-lg">
              <p className="font-medium">Viral Clipes</p>
              <p className="text-muted-foreground">Email: legal@viralclipes.com</p>
              <p className="text-muted-foreground">Website: https://viralclipes.com</p>
            </div>
          </section>

          {/* Footer Links */}
          <div className="flex flex-wrap gap-4 pt-8 border-t border-border">
            <Link to="/privacy" className="text-primary hover:underline">
              Política de Privacidade
            </Link>
            <Link to="/" className="text-primary hover:underline">
              Página Inicial
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center text-muted-foreground text-sm">
          <p>&copy; {new Date().getFullYear()} Viral Clipes. Todos os direitos reservados.</p>
        </div>
      </footer>
    </div>
  );
}
